import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { UserCog, Plus, Edit, Trash2, Phone, Mail, Award, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const TechniciansPage = () => {
  const navigate = useNavigate();
  const [technicians, setTechnicians] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    employee_id: '',
    certifications: [],
    specializations: [],
    notes: ''
  });

  const availableSpecializations = [
    'Fire Extinguisher',
    'Fire Hose Reel',
    'Fire Hydrant',
    'Fire Blanket',
    'Exit & Emergency Lighting',
    'Fire Doors',
    'Sprinkler Systems',
    'Fire Alarm Systems'
  ];

  useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/technicians`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTechnicians(response.data);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load technicians');
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (tech = null) => {
    if (tech) {
      setEditingTech(tech);
      setFormData({
        first_name: tech.first_name,
        last_name: tech.last_name,
        email: tech.email,
        phone: tech.phone,
        employee_id: tech.employee_id || '',
        certifications: tech.certifications || [],
        specializations: tech.specializations || [],
        notes: tech.notes || ''
      });
    } else {
      setEditingTech(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        employee_id: '',
        certifications: [],
        specializations: [],
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingTech) {
        await axios.put(`${API}/technicians/${editingTech.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Technician updated successfully!');
      } else {
        await axios.post(`${API}/technicians`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Technician created successfully!');
      }

      setDialogOpen(false);
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save technician');
    }
  };

  const handleDelete = async (techId, techName) => {
    if (!confirm(`Are you sure you want to delete ${techName}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/technicians/${techId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Technician deleted successfully!');
      fetchTechnicians();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete technician');
    }
  };

  const toggleSpecialization = (spec) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading technicians...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <UserCog className="w-8 h-8 text-orange-500" />
                Technicians
              </h1>
              <p className="text-gray-400 mt-2">Manage field technicians and their specializations</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Technician
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 text-white border-gray-700">
              <DialogHeader>
                <DialogTitle>{editingTech ? 'Edit Technician' : 'Add New Technician'}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employee ID (Optional)</Label>
                  <Input
                    id="employee_id"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Specializations</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableSpecializations.map(spec => (
                      <Button
                        key={spec}
                        type="button"
                        variant={formData.specializations.includes(spec) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleSpecialization(spec)}
                        className={formData.specializations.includes(spec) 
                          ? "bg-orange-600 hover:bg-orange-700" 
                          : "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"}
                      >
                        {spec}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
                  Cancel
                </Button>
                <Button onClick={handleSubmit} className="bg-orange-600 hover:bg-orange-700">
                  {editingTech ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Technicians Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {technicians.map((tech) => (
            <Card key={tech.id} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <UserCog className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{tech.first_name} {tech.last_name}</CardTitle>
                      {tech.employee_id && (
                        <p className="text-xs text-gray-400">ID: {tech.employee_id}</p>
                      )}
                    </div>
                  </div>
                  <Badge className={tech.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                    {tech.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Mail className="w-4 h-4" />
                    <span>{tech.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Phone className="w-4 h-4" />
                    <span>{tech.phone}</span>
                  </div>
                </div>

                {tech.specializations && tech.specializations.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Award className="w-3 h-3" />
                      <span>Specializations</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {tech.specializations.slice(0, 3).map((spec, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/30">
                          {spec}
                        </Badge>
                      ))}
                      {tech.specializations.length > 3 && (
                        <Badge variant="outline" className="text-xs bg-gray-700 text-gray-400">
                          +{tech.specializations.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenDialog(tech)}
                    className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(tech.id, `${tech.first_name} ${tech.last_name}`)}
                    className="bg-red-900/20 border-red-900/30 text-red-400 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {technicians.length === 0 && (
          <Card className="bg-gray-800 border-gray-700 text-center p-12">
            <UserCog className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No technicians found</h3>
            <p className="text-gray-400 mb-6">Add your first technician to get started</p>
            <Button onClick={() => handleOpenDialog()} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First Technician
            </Button>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default TechniciansPage;
